import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatMenuTrigger } from '@angular/material/menu';
import * as moment from 'moment';
import { GetAllInOrdersParams } from 'src/app/core/data-sources/v3/in-orders/in-orders-data-source.service';
import { DEFAULT_DATE_FORMAT } from 'src/app/helpers/date-format.helper';
import { InOrderState } from 'src/models/InOrder';

@Component({
  selector: 'app-in-group-buy-order-header',
  templateUrl: './in-group-buy-order-header.component.html',
  styleUrls: ['./in-group-buy-order-header.component.scss'],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: DEFAULT_DATE_FORMAT }]
})
export class InGroupBuyOrderHeaderComponent implements OnInit, OnChanges {

  @Output() filterValues = new EventEmitter<{
    filterParams: GetAllInOrdersParams,
    sortParams: Pick<GetAllInOrdersParams, 'orderBy' | 'sortBy'>
  }>();
  @Input() activeFilterParams: GetAllInOrdersParams = {};
  @Input() activeSortParams: Pick<GetAllInOrdersParams, 'orderBy' | 'sortBy'>;

  maxDate = new Date();
  minDate: Date;
  showCheckIcon: { [key: string]: boolean } = {};
  appliedFilters: { [key: string]: boolean } = {};
  sortDirection: 'ASC' | 'DESC';
  sortColumn = '';
  sortParams: Pick<GetAllInOrdersParams, 'orderBy' | 'sortBy'>;
  filterParams: GetAllInOrdersParams = {};

  entrySourceOptions = [
    { label: 'Interna', value: 'MANUAL' as const },
    { label: 'Nota fiscal', value: 'INVOICE_XML' as const }
  ];
  stateOptions = [
    { label: 'Criada', value: InOrderState.CREATED },
    { label: 'Em recebimento', value: InOrderState.RECEIVING },
    { label: 'Finalizada', value: InOrderState.FINISHED }
  ];

  form = new FormGroup({
    startDate: new FormControl(null),
    endDate: new FormControl(null),
    search: new FormControl(null),
    entrySource: new FormControl(null),
    state: new FormControl(null)
  });

  constructor() { }

  ngOnInit(): void {
  }

  ngOnChanges(): void {
    this.filterParams = { ...this.activeFilterParams };
    this.appliedFilters = {};
    Object.keys(this.filterParams).forEach(key => this.setAppliedFilter(key));
    this.sortParams = this.activeSortParams;
    this.sortColumn = this.activeSortParams?.orderBy || '';
    this.form.patchValue({
      startDate: this.activeFilterParams?.issuedAtStartDate
        ? moment(this.activeFilterParams.issuedAtStartDate)
        : null,
      endDate: this.activeFilterParams?.issuedAtEndDate
        ? moment(this.activeFilterParams.issuedAtEndDate)
        : null,
      search: this.activeFilterParams?.search || null,
      entrySource: this.activeFilterParams?.entrySource || null,
      state: this.activeFilterParams?.state || null
    }, { emitEvent: false });
  }

  get isIssuedAtSaveBtnDisabled(): boolean {
    const start = this.form.get('startDate');
    const end = this.form.get('endDate');
    const bothDatesValid = start?.valid && end?.valid;
    const bothDatesEmpty = !start?.value && !end?.value;

    return this.showCheckIcon['issuedAt']
      ? !(bothDatesValid || bothDatesEmpty)
      : !bothDatesValid;
  }

  sort(orderBy: GetAllInOrdersParams['orderBy'], sortBy: GetAllInOrdersParams['sortBy']) {
    this.sortParams = null;
    this.showCheckIcon = {};
    this.sortDirection = sortBy;
    this.showCheckIcon[orderBy] = true;
    this.sortParams = { orderBy, sortBy };
  }

  applyFilters(menu: MatMenuTrigger) {
    const startDate = this.form.value.startDate
      ? moment(this.form.value.startDate._d).valueOf()
      : null;
    const endDate = this.form.value.endDate
      ? moment(this.form.value.endDate._d).endOf('day').valueOf()
      : null;

    this.filterParams = {
      ...this.activeFilterParams,
      search: this.form.value.search,
      entrySource: this.form.value.entrySource,
      issuedAtStartDate: startDate,
      issuedAtEndDate: endDate,
      state: this.form.value.state
    };

    this.appliedFilters = {};
    this.removeEmptyKeys(this.filterParams);
    this.sortColumn = this.sortParams?.orderBy;
    this.filterValues.emit({
      filterParams: this.filterParams,
      sortParams: this.sortParams
    });
    menu.closeMenu();
  }

  clearFilters(menu: MatMenuTrigger, filter: string) {
    this.maxDate = new Date();
    this.minDate = null;
    this.sortColumn = this.sortColumn === filter ? null : this.sortColumn;
    this.sortParams = this.sortParams?.orderBy === filter ? null : this.sortParams;
    this.filterParams = { ...this.activeFilterParams };
    delete this.appliedFilters[filter];
    delete this.showCheckIcon[filter];

    switch (filter) {
      case 'orderNumber':
        this.form.get('search').reset();
        delete this.filterParams.search;
        break;
      case 'issuedAt':
        this.form.get('startDate').reset();
        this.form.get('endDate').reset();
        delete this.filterParams.issuedAtStartDate;
        delete this.filterParams.issuedAtEndDate;
        break;
      case 'entrySource':
        this.form.get('entrySource').reset();
        delete this.filterParams.entrySource;
        break;
      case 'state':
        this.form.get('state').reset();
        delete this.filterParams.state;
        break;
    }

    this.filterValues.emit({
      filterParams: this.filterParams,
      sortParams: this.sortParams
    });
    menu.closeMenu();
  }

  removeEmptyKeys(params: GetAllInOrdersParams) {
    Object.keys(params).forEach(key => {
      if (params[key] === null || params[key] === undefined || params[key] === '') {
        delete params[key];
      } else {
        this.setAppliedFilter(key);
      }
    });
  }

  setAppliedFilter(name: string) {
    switch (name) {
      case 'search':
        this.appliedFilters.orderNumber = true;
        break;
      case 'issuedAtStartDate':
      case 'issuedAtEndDate':
        this.appliedFilters.issuedAt = true;
        break;
      default:
        this.appliedFilters[name] = true;
    }
  }

  onStartDateChange() {
    this.minDate = this.form.value.startDate?._d;
  }

  onEndDateChange() {
    this.maxDate = this.form.value.endDate?._d || new Date();
  }

}
